import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';

const policies = {
  'privacy-policy': {
    title: 'Privacy Policy',
    lastUpdated: 'February 2025',
    sections: [
      {
        heading: 'Information We Collect',
        content:
          'We collect information you provide directly to us, such as when you fill out a contact form, subscribe to our newsletter, or communicate with us. This may include your name, email address, phone number, company name, and any other information you choose to provide.',
      },
      {
        heading: 'How We Use Your Information',
        content:
          'We use the information we collect to respond to your inquiries, provide our services, send you updates and marketing communications (with your consent), improve our website and services, and comply with legal obligations.',
      },
      {
        heading: 'Data Security',
        content:
          'We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. We use industry-standard encryption and secure server infrastructure.',
      },
      {
        heading: 'Data Sharing',
        content:
          'We do not sell, trade, or rent your personal information to third parties. We may share your information with trusted service providers who assist us in operating our website and conducting our business, subject to confidentiality agreements.',
      },
      {
        heading: 'Your Rights',
        content:
          'You have the right to access, correct, or delete your personal information. You may also object to or restrict certain processing of your data. To exercise these rights, please contact us at info@techmascot.in.',
      },
      {
        heading: 'Contact Us',
        content:
          'If you have any questions about this Privacy Policy, please contact us at info@techmascot.in or visit our Contact page.',
      },
    ],
  },
  'terms-of-service': {
    title: 'Terms of Service',
    lastUpdated: 'February 2025',
    sections: [
      {
        heading: 'Acceptance of Terms',
        content:
          'By accessing and using the Techmascot Technologies website and services, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.',
      },
      {
        heading: 'Use of Services',
        content:
          'Our services are intended for business use. You agree to use our services only for lawful purposes and in accordance with these terms. You must not use our services in any way that violates applicable laws or regulations.',
      },
      {
        heading: 'Intellectual Property',
        content:
          'All content on this website, including text, graphics, logos, and software, is the property of Techmascot Technologies Private Limited and is protected by applicable intellectual property laws. You may not reproduce or distribute any content without our written permission.',
      },
      {
        heading: 'Limitation of Liability',
        content:
          'Techmascot Technologies shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our services or website. Our total liability shall not exceed the amount paid for the specific service giving rise to the claim.',
      },
      {
        heading: 'Service Modifications',
        content:
          'We reserve the right to modify or discontinue any part of our services at any time. We will provide reasonable notice of significant changes where possible.',
      },
      {
        heading: 'Governing Law',
        content:
          'These terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in India.',
      },
    ],
  },
  'cookie-policy': {
    title: 'Cookie Policy',
    lastUpdated: 'February 2025',
    sections: [
      {
        heading: 'What Are Cookies',
        content:
          'Cookies are small text files stored on your device when you visit our website. They help us provide a better browsing experience, analyze site traffic, and understand where our visitors are coming from.',
      },
      {
        heading: 'Types of Cookies We Use',
        content:
          'We use essential cookies (required for the website to function), analytics cookies (to understand how visitors interact with our site), and preference cookies (to remember your settings and preferences).',
      },
      {
        heading: 'Essential Cookies',
        content:
          'These cookies are necessary for the website to function properly. They enable core functionality such as security, network management, and accessibility. You cannot opt out of these cookies.',
      },
      {
        heading: 'Analytics Cookies',
        content:
          'We use analytics cookies to understand how visitors use our website. This helps us improve the user experience. All data collected is anonymized and aggregated.',
      },
      {
        heading: 'Managing Cookies',
        content:
          'You can control and manage cookies through your browser settings. Please note that disabling certain cookies may affect the functionality of our website.',
      },
      {
        heading: 'Contact Us',
        content:
          'If you have questions about our use of cookies, please contact us at info@techmascot.in.',
      },
    ],
  },
};

function PolicyPage() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, '');
  const policy = policies[slug];

  if (!policy) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 24px', textAlign: 'center' }}>
        <h2 style={{ fontSize: '2rem', fontFamily: 'Poppins, sans-serif', color: '#0F172A', marginBottom: '16px' }}>Page Not Found</h2>
        <p style={{ color: '#94A3B8', marginBottom: '28px' }}>The page you're looking for doesn't exist.</p>
        <Link to="/" className="btn-primary">Go Home</Link>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '72px', minHeight: '100vh', background: '#F5F7F5' }}>
      <Helmet>
        <title>{policy.title} | Techmascot Technologies</title>
        <meta name="description" content={`Read the ${policy.title} of Techmascot Technologies Private Limited.`} />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={`https://www.techmascot.com${location.pathname}`} />
      </Helmet>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #040d24 0%, #07153a 60%, #0e2a6e 100%)', padding: '80px 0 60px', textAlign: 'center' }}>
        <div className="container">
          <h1 style={{ fontSize: '2.5rem', fontWeight: 800, color: '#FFFFFF', fontFamily: 'Poppins, sans-serif', marginBottom: '12px' }}>
            {policy.title}
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem' }}>
            Last updated: {policy.lastUpdated}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="container" style={{ maxWidth: '800px', padding: '64px 24px' }}>
        <Link
          to="/"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: '#003083', fontWeight: 600, fontSize: '0.9rem', marginBottom: '40px' }}
        >
          <FaArrowLeft /> Back to Home
        </Link>

        <div style={{ background: '#FFFFFF', borderRadius: '16px', padding: '48px', boxShadow: '0 4px 24px rgba(0,48,131,0.08)', border: '1px solid #CBD5E0' }}>
          {policy.sections.map((section, i) => (
            <div key={i} style={{ marginBottom: i < policy.sections.length - 1 ? '36px' : 0 }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0F172A', fontFamily: 'Poppins, sans-serif', marginBottom: '12px' }}>
                {section.heading}
              </h3>
              <p style={{ fontSize: '0.95rem', color: '#94A3B8', lineHeight: 1.8 }}>
                {section.content}
              </p>
              {i < policy.sections.length - 1 && (
                <hr style={{ border: 'none', borderTop: '1px solid #CBD5E0', marginTop: '36px' }} />
              )}
            </div>
          ))}
        </div>

        <p style={{ textAlign: 'center', marginTop: '40px', fontSize: '0.875rem', color: '#94A3B8' }}>
          Questions? <Link to="/contact" style={{ color: '#003083', fontWeight: 600 }}>Contact us</Link>
        </p>
      </div>
    </div>
  );
}

export default PolicyPage;
